import { action, makeAutoObservable } from "mobx";
import { toast } from "react-toastify";
import { components } from "../../../../../api/spec";
import {
  sitchEventAcceptRound,
  sitchEventListCandidatesForRound,
  sitchEventRegenerateRound,
  sitchEventRejectCandidateFromRound,
  sitchEventTaskStatus,
} from "../../../../../api/sitch_events";
import EventStore from "../../event/store/eventStore";

export class SetupStore {
  activeRound = 1;

  isLoading = false;

  error: string | null = null;

  candidates: components["schemas"]["EventCandidateSummarySchema"][] = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  @action
  setActiveRound = async (roundNumber: number) => {
    if (roundNumber !== 1) {
      const previousRound = EventStore.sitchEventData?.rounds?.find(
        (r) => r.round_num === roundNumber - 1,
      );

      if (!previousRound?.is_accepted) {
        throw new Error(
          `Round ${roundNumber} is not accessible until the previous round is accepted`,
        );
      }
    }

    const targetRound = EventStore.sitchEventData?.rounds?.find(
      (round) => round.round_num === roundNumber,
    );

    if (!targetRound?.guid) {
      this.candidates = [];
      this.activeRound = roundNumber;
      return;
    }

    this.activeRound = roundNumber;
    this.isLoading = true;
    try {
      await this.loadCandidatesForRound(targetRound.guid);
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to load round data";
    } finally {
      this.isLoading = false;
    }
  };

  @action
  async rejectCandidate(candidateGuid: string) {
    if (!this.currentRound?.guid) {
      throw new Error("No round to reject candidate from");
    }

    this.isLoading = true;
    this.error = null;
    try {
      await sitchEventRejectCandidateFromRound({
        candidate: candidateGuid,
        round: this.currentRound.guid,
      });
      await this.loadCandidatesForRound(this.currentRound.guid);
      toast.success(`Candidate '${candidateGuid}' rejected!`);
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to reject candidate";
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async acceptRound() {
    if (!this.currentRound?.guid) {
      throw new Error("No round to accept");
    }

    this.isLoading = true;
    this.error = null;
    try {
      const acceptResponse = await sitchEventAcceptRound({
        round: this.currentRound.guid,
      });
      if (!acceptResponse.task_id) {
        throw new Error("No task ID returned from accept round");
      }
      await SetupStore.pollForTaskCompletion(acceptResponse.task_id);
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to accept round";
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async loadCandidatesForRound(roundGuid: string) {
    this.isLoading = true;
    this.error = null;
    try {
      this.candidates = await sitchEventListCandidatesForRound(roundGuid);
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to load candidates";
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async regenerateSetup() {
    if (!this.currentRound?.guid) {
      throw new Error("No round to regenerate");
    }

    this.isLoading = true;
    this.error = null;
    try {
      const regenerateResponse = await sitchEventRegenerateRound({
        round: this.currentRound.guid,
      });
      if (!regenerateResponse.task_id) {
        throw new Error("No task ID returned from regenerate round");
      }
      await SetupStore.pollForTaskCompletion(regenerateResponse.task_id);
      this.candidates = await sitchEventListCandidatesForRound(
        this.currentRound.guid,
      );
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : "Failed to regenerate setup";
    } finally {
      this.isLoading = false;
    }
  }

  static async pollForTaskCompletion(taskId: string) {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const statusResponse = await sitchEventTaskStatus(taskId);
      if (statusResponse.ready) {
        return;
      }
      // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
      await new Promise<void>((resolve) => setTimeout(resolve, 2000));
    }
  }

  get currentRound() {
    if (!EventStore.sitchEventData?.rounds) return null;
    return (
      EventStore.sitchEventData.rounds.find(
        (round) => round.round_num === this.activeRound,
      ) || null
    );
  }
}

export default new SetupStore();
